import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';

export const PAGE_SIZE_OPTIONS = [15, 25, 30];

// Client-side pagination hook. Slices `items` into the current page and keeps
// the page in range as the list (filters/search) changes.
export function usePagination(items, defaultSize = 15) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(defaultSize);
  const total = items?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const setPageSize = (size) => { setPageSizeState(size); setPage(1); };
  const start = (page - 1) * pageSize;
  const pageItems = useMemo(
    () => (items || []).slice(start, start + pageSize),
    [items, start, pageSize]
  );

  return { page, setPage, pageSize, setPageSize, total, totalPages, start, pageItems };
}

export function TablePagination({ page, setPage, pageSize, setPageSize, total, totalPages, start, testid = 'pagination', className = '' }) {
  if (total === 0) return null;
  const from = start + 1;
  const to = Math.min(start + pageSize, total);

  return (
    <div
      data-testid={testid}
      className={`flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 ${className}`}
    >
      <div className="flex items-center gap-2">
        <span className="font-body text-xs text-foreground/60">Rows per page</span>
        <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
          <SelectTrigger data-testid={`${testid}-size`} className="w-[72px] h-8 rounded-lg bg-card border-border font-body text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            {PAGE_SIZE_OPTIONS.map((o) => (
              <SelectItem key={o} value={String(o)} className="font-body text-sm">{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-3">
        <span className="font-body text-xs text-foreground/60" data-testid={`${testid}-range`}>
          {from}–{to} of {total}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid={`${testid}-prev`}
            className="h-8 w-8 p-0 rounded-lg bg-card border-border disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-body text-xs text-foreground/80 px-2 tabular-nums" data-testid={`${testid}-page`}>
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid={`${testid}-next`}
            className="h-8 w-8 p-0 rounded-lg bg-card border-border disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
